import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { MessageSquare, AlertTriangle, Check, Loader2, Info, Bell } from 'lucide-react';
import { apiService } from '../../services/api';

/**
 * Botão para enviar lembrete de pagamento
 * Aparece para pagamentos que vencem HOJE ou estão ATRASADOS
 */
export const LembreteAtrasoButton = ({ pagamento, onSuccess }) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calculando, setCalculando] = useState(false);
  const [valoresAtraso, setValoresAtraso] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Verificar se o pagamento vence hoje ou está atrasado
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  
  let dataVencimento;
  if (typeof pagamento.data_vencimento === 'string' && pagamento.data_vencimento.match(/^\d{4}-\d{2}-\d{2}/)) {
    const [year, month, day] = pagamento.data_vencimento.split('T')[0].split('-');
    dataVencimento = new Date(year, month - 1, day);
  } else {
    dataVencimento = new Date(pagamento.data_vencimento);
  }
  dataVencimento.setHours(0, 0, 0, 0);
  
  const estaPago = pagamento.status === 'pago';
  const estaAtrasado = dataVencimento < hoje && !estaPago;
  const venceHoje = dataVencimento.getTime() === hoje.getTime() && !estaPago;

  // Mostrar botão se vence HOJE ou está ATRASADO
  if (!estaAtrasado && !venceHoje) {
    return null;
  }

  const handleCalcularValores = async () => {
    setCalculando(true);
    setError(null);
    
    try {
      const valores = await apiService.calcularValoresAtraso(pagamento.id);
      setValoresAtraso(valores);
    } catch (err) {
      setError('Erro ao calcular valores de atraso');
      console.error(err);
    } finally {
      setCalculando(false);
    }
  };

  const handleEnviarLembrete = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      const response = await apiService.enviarLembreteAtraso(pagamento.id);
      
      // Se retornou link do WhatsApp, abrir em nova aba
      if (response.whatsapp_link) {
        window.open(response.whatsapp_link, '_blank');
        setSuccess(true);
        
        // Fechar modal após 1 segundo
        setTimeout(() => {
          setShowModal(false);
          setSuccess(false);
          setValoresAtraso(null);
          if (onSuccess) onSuccess();
        }, 1500);
      } else {
        setError('Link do WhatsApp não foi gerado');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao gerar lembrete');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
    handleCalcularValores();
  };

  // Calcular dias de atraso corretamente
  const calcularDiasAtraso = () => {
    if (valoresAtraso?.dias_atraso !== undefined) return valoresAtraso.dias_atraso;
    const diffTime = hoje - dataVencimento;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };
  const diasAtraso = calcularDiasAtraso();

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleOpenModal}
        className={venceHoje 
          ? "text-blue-600 border-blue-300 hover:bg-blue-50" 
          : "text-orange-600 border-orange-300 hover:bg-orange-50"}
      >
        {venceHoje ? <Bell className="h-4 w-4 mr-2" /> : <MessageSquare className="h-4 w-4 mr-2" />}
        {venceHoje ? "Lembrete Normal" : "Enviar Lembrete"}
      </Button>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${venceHoje ? 'text-blue-600' : 'text-orange-600'}`}>
              {venceHoje ? <Bell className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
              {venceHoje ? 'Enviar Lembrete de Pagamento' : 'Enviar Lembrete de Pagamento em Atraso'}
            </DialogTitle>
            <DialogDescription>
              {venceHoje 
                ? 'Envie um lembrete via WhatsApp para o cliente sobre o pagamento que vence hoje'
                : 'Envie um lembrete personalizado via WhatsApp para o cliente sobre o pagamento vencido'}
            </DialogDescription>
          </DialogHeader>

          {calculando ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-3 text-gray-600">Calculando valores...</span>
            </div>
          ) : valoresAtraso ? (
            <div className="space-y-4">
              {/* Informações do Atraso */}
              <Alert className={valoresAtraso.dias_atraso > 2 ? "border-red-200 bg-red-50" : "border-yellow-200 bg-yellow-50"}>
                <AlertTriangle className={valoresAtraso.dias_atraso > 2 ? "h-4 w-4 text-red-600" : "h-4 w-4 text-yellow-600"} />
                <AlertDescription className={valoresAtraso.dias_atraso > 2 ? "text-red-800" : "text-yellow-800"}>
                  <strong>Pagamento vencido há {valoresAtraso.dias_atraso} dia(s)</strong>
                  <br />
                  {valoresAtraso.dias_tolerancia_restantes !== undefined && valoresAtraso.dias_tolerancia_restantes > 0 ? (
                    <span>Ainda dentro do período de tolerância ({valoresAtraso.dias_tolerancia_restantes} dia(s) restantes)</span>
                  ) : (
                    <span className="font-bold">⚠️ Período de tolerância expirado - Veículo pode ser bloqueado</span>
                  )}
                </AlertDescription>
              </Alert>

              {/* Cálculo de Valores */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-gray-900 mb-3">💰 Cálculo de Valores:</h4>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Aluguel Semanal:</span>
                  <span className="font-medium">R$ {valoresAtraso.valor_original.toFixed(2)}</span>
                </div>

                {valoresAtraso.multa > 0 && (
                  <>
                    <div className="flex justify-between text-red-600">
                      <span>Multa:</span>
                      <span className="font-medium">+ R$ {valoresAtraso.multa.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span>Juros ({valoresAtraso.dias_atraso || valoresAtraso.dias_apos_tolerancia} dia(s)):</span>
                      <span className="font-medium">+ R$ {valoresAtraso.juros.toFixed(2)}</span>
                    </div>
                  </>
                )}

                {/* Débitos em aberto */}
                {valoresAtraso.valor_debitos_parcela > 0 && (
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex justify-between text-purple-600">
                      <span>Parcela Débitos ({valoresAtraso.parcela_numero || 1}/4):</span>
                      <span className="font-medium">+ R$ {valoresAtraso.valor_debitos_parcela.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Saldo restante: R$ {valoresAtraso.total_debitos?.toFixed(2)} 
                      {valoresAtraso.parcelas_restantes > 0 && ` (${valoresAtraso.parcelas_restantes} parcela(s) restante(s))`}
                    </p>
                  </div>
                )}

                <div className="pt-3 border-t border-gray-300 flex justify-between">
                  <span className="font-bold text-lg">Valor Total a Pagar:</span>
                  <span className="font-bold text-lg text-green-600">R$ {valoresAtraso.valor_total.toFixed(2)}</span>
                </div>
              </div>

              {/* Informações sobre a Mensagem */}
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>O lembrete incluirá:</strong>
                  <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                    <li>Dados do pagamento e veículo</li>
                    <li>Cálculo detalhado com multa e juros (se aplicável)</li>
                    {valoresAtraso.valor_debitos_parcela > 0 && (
                      <li className="text-purple-600 font-medium">Parcela {valoresAtraso.parcela_numero || 1}/4 de débitos em aberto</li>
                    )}
                    <li>Aviso sobre período de tolerância</li>
                    <li>Informação sobre bloqueio/recolhimento do veículo</li>
                    <li>Formas de pagamento disponíveis (PIX)</li>
                  </ul>
                </AlertDescription>
              </Alert>

              {/* Mensagens de Sucesso/Erro */}
              {success && (
                <Alert className="border-green-200 bg-green-50">
                  <Check className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    ✅ WhatsApp aberto com a mensagem pronta! Basta clicar em &quot;Enviar&quot; no WhatsApp.
                  </AlertDescription>
                </Alert>
              )}

              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {error}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleEnviarLembrete}
              disabled={loading || calculando || success}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : success ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Enviado!
                </>
              ) : (
                <>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Enviar Lembrete via WhatsApp
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
